
import { Component, EventEmitter, Input, Output, OnChanges, forwardRef, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BaseFormWidgetComponent } from '../../../shared/base-form.component';

import * as moment_api from 'moment';
const moment = moment_api;

export interface ICalOptions {
    from?: number; // Earliest day that can be selected
    to?: number; // Latest day that can be selected
    format?: {
        day: string, // Formatting for the days of the week
        month: string, // Formatting for the displayed month
    };
}

@Component({
    selector: 'calendar',
    template: `
<div [class]="'calendar' + (klass ? ' ' + klass : '') + (disabled ? ' disable' : '')" widget>
    <div class="header">
        <div class="display">
            <div class="change previous" *ngIf="data.limit !== 0" [class.disabled]="disable_prev" (click)="changeMonth(-1)">&#706;</div>
            <div class="month" (dblclick)="changeMonth(-data.offset)">{{ display }}</div>
            <div class="change next" *ngIf="data.limit !== 0" [class.disabled]="disable_next" (click)="changeMonth(1)">&#707;</div>
        </div>
        <div class="week">
            <div class="day" *ngFor="let day of data.weekdays">{{ day }}</div>
        </div>
    </div>
    <div class="body">
        <div class="month">
            <div class="week" *ngFor="let week of data.month">
                <div class="day" *ngFor="let day of week">
                    <div class="content"
                        [class.disabled]="day.disabled"
                        [class.active]="day.active && !disabled"
                        [class.non-month]="!day.this_month"
                        [class.event]="day.events && !day.disabled && day.this_month"
                        (touchrelease)="day.this_month ? setDate(day) : ''">
                        {{ day.date }}
                        <div class="events" *ngIf="day.events && !day.disabled && day.this_month">{{day.events}}</div>
                    </div>
                    <div class="bar" *ngIf="day.today"></div>
                </div>
            </div>
        </div>
    </div>
</div>
`,
    styles: [`.calendar{width:25em;border:1px solid #f2f2f2}.calendar *{-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.calendar.disable .body *{cursor:default}.header{color:rgba(0,0,0,.87);border-bottom:1px solid #f0f0f0}.header .week{margin:.5em 0}.change{height:1em;width:1em;margin:.25em .75em .5em;font-size:1.5em;cursor:pointer}.change:hover{opacity:.8}.change.disabled{color:rgba(0,0,0,.87);opacity:.2}.change.disabled:hover{color:rgba(0,0,0,.87);opacity:.2;background:0 0}.display{display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-align:center;-ms-flex-align:center;align-items:center}.display .month{-webkit-box-flex:1;-ms-flex:1;flex:1;text-align:center}.body .month{padding:.25em 0}.body .day{height:2.85em;width:2.5em;margin:.25em}.week{display:-webkit-box;display:-ms-flexbox;display:flex;font-weight:400}.day{position:relative;-webkit-box-flex:1;-ms-flex:1;flex:1;text-align:center}.content{position:relative;border-radius:100%;height:2.5em;width:2.5em;padding:.45em .1em;margin:.25em;display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-align:center;-ms-flex-align:center;align-items:center;-webkit-box-pack:center;-ms-flex-pack:center;justify-content:center;cursor:pointer;border:1px solid #fff}.content:hover{background-color:#ccc;opacity:.8}.events{position:absolute;top:0;right:0;-webkit-transform:translate(40%,-40%);transform:translate(40%,-40%);font-size:.7em;height:2em;width:2em;border-radius:1.05em;display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-align:center;-ms-flex-align:center;align-items:center;-webkit-box-pack:center;-ms-flex-pack:center;justify-content:center;background-color:#2196f3;color:#fff;border:2px solid #fff}.event{background-color:#37474f;color:#fff}.disabled{background-color:#eee;color:rgba(255,255,255,.87);cursor:default;opacity:.54}.disabled:hover{opacity:1;background-color:#eee;color:rgba(255,255,255,.87)}.bar{position:absolute;bottom:0;left:50%;-webkit-transform:translateX(-50%);transform:translateX(-50%);background-color:#673ab7;height:8px;width:8px;border:2px solid #fff;border-radius:5px}.active{background-color:#2196f3;border-color:#2196f3;color:#fff}.active:hover{background-color:#2196f3}.non-month{color:rgba(0,0,0,.2);background:0 0;cursor:default}.non-month:hover{color:rgba(0,0,0,.2);background:0 0;opacity:1}`],
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CalendarComponent),
        multi: true
      }
    ]
})
export class CalendarComponent extends BaseFormWidgetComponent<number> implements OnChanges, ControlValueAccessor {
    @Input() public date: number; // Unix timestamp with milliseconds
    @Input() public today: number; // Unix timestamp with milliseconds
    @Input() public events: { [name:string]: number } = {};
    @Input() public range: { start: number, end: number }; // Set range as active
    @Input() public options: ICalOptions;
    @Output() public dateChange = new EventEmitter<number>();
    @Output() public month = new EventEmitter<number>();

    public display: string;
    public disable_next: boolean;
    public disable_prev: boolean;

    public ngOnInit() {
        this.data.offset = 0;
        if (!this.model) {
            this.model = (new Date()).getTime();
        }
        if (!this.options) {
            this.options = {};
        }
        this.generateMonth();
    }

    public ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        if (changes.date || changes.model) {
            if (changes.date) { this.model = this.date; }
            const now = moment(this.today).date(1).startOf('d');
            const duration = moment.duration(moment(this.model).diff(now));
            this.data.offset = Math.floor(duration.asMonths());
            this.generateMonth();
        }
        if (changes.options) {
            if (!this.options) { this.options = {}; }
            if (this.options.from) {
                this.data.from = moment(this.options.from);
                const end = moment(this.data.from).endOf('M')
                const now = moment().startOf('M');
                this.data.from_limit = Math.ceil(moment.duration(end.diff(now)).asMonths()) - 1;
            } else {
                this.data.from = null;
            }
            if (this.options.to) {
                this.data.to = moment(this.options.to);
            } else {
                this.data.to = null;
            }
            this.changeMonth();
        }
        if (changes.today) {
            const now = moment().startOf('d');
            if (!this.today || this.today < now.valueOf()) {
                this.today = now.valueOf();
            }
        }
        if (changes.events) {
            this.generateMonth();
        }
    }

    /**
     * Update local value when form control value is changed
     * @param value
     */
    public writeValue(value: number) {
        this.setDate({ timestamp: value });
    }

    public setDate(day: { timestamp: number }, emit: boolean = true) {
        if (this.disabled) { return; }
        const now = moment(this.today);
        const date = moment(day.timestamp);
        if ((!this.data.from || date.isSameOrAfter(this.data.from, 'd')) && (!this.data.to || date.isSameOrBefore(this.data.to, 'd'))) {
            this.model = day.timestamp;
            if (emit) {
                this.modelChange.emit(this.model);
                this.dateChange.emit(this.model);
                this.change(this.model)
            }
        }
        this.generateMonth();
    }

    public changeMonth(value: number = 0) {
        let new_month = this.data.offset + value;
        const now = moment().add(new_month, 'M');
        if ((!this.data.from || now.isSameOrAfter(this.data.from, 'M')) &&
            (!this.data.to || now.isSameOrBefore(this.data.to, 'M')))  {
            this.data.offset = new_month || value || 0;
            this.month.emit(this.data.offset);
            this.generateMonth();
        }
    }

    private generateWeekdays(format: string = 'dd') {
        const week: string[] = [];
        const weekday = moment();
        weekday.date(weekday.date() - weekday.day());
        for (let i = 0; i < 7; i++) {
            week.push(weekday.format(format));
            weekday.add(1, 'days');
        }
        this.data.weekdays = week;
    }

    private generateMonth() {
        const set_date = moment(this.model);
        const date = moment();
        const today = moment();
        date.add(this.data.offset || 0, 'M');
        const active_month = moment(date);
            // Create display for month
        if (this.options && this.options.format) {
            this.display = date.format(this.options.format.month || 'MMMM YYYY');
            const format = this.options.format.day || 'dd';
            this.generateWeekdays(format);
        } else {
            this.display = date.format('MMMM YYYY');
            this.generateWeekdays();
        }
            // Create displays for weekdays
        date.date(1);
        if (date.day() > 0) {
            date.date(date.date() - date.day());
        }
        const month: { [name: string]: any }[][] = [];
        for (let i = 0; i < 6; i++) {
            const week: { [name: string]: any }[] = [];
            for (let d = 0; d < 7; d++) {
                const day = {
                    timestamp: date.valueOf(),
                    date: date.date(),
                    active: date.isSame(set_date, 'd'),
                    today: date.isSame(today, 'd'),
                    events: this.events ? this.events[date.format('YYYY-MM-DD')] || 0 : 0,
                    disabled: (this.data.from && date.isBefore(this.data.from, 'd')) || (this.data.to && date.isAfter(this.data.to, 'd')),
                    this_month: date.isSame(active_month, 'M'),
                };
                week.push(day);
                date.add(1, 'days');
            }
            month.push(week);
        }
        this.checkOffset();
        this.data.month = month;
    }

    public checkOffset() {
        const today = moment().startOf('M');
        this.disable_prev = false;
        this.disable_next = false;
        if (this.options) {
            if (this.options.from) {
                const from = moment(this.options.from).startOf('M');
                const past_offset = Math.floor(moment.duration(today.diff(from)).asMonths()) || 0;
                if (this.data.offset <= -past_offset) {
                    this.disable_prev = true;
                }
            }
            if (this.options.to) {
                const to = moment(this.options.to).startOf('M');
                const future_offset = Math.floor(moment.duration(to.diff(today)).asMonths())|| 0;
                if (this.data.offset >= future_offset) {
                    this.disable_next = true;
                }
            }
        }
    }
}
